import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';

const images_machines = [
  { src: '/images/inside_cnc.jpg',          alt: 'Inside CNC lathe machining precision component at Mato Industries Delhi' },
  { src: '/images/doosan_lynx_2100lm.jpg',  alt: 'Doosan Lynx 2100LM CNC lathe for high-precision component machining at Mato Industries' },
  { src: '/images/vc-ez-ip-410.webp',       alt: 'CNC machine used for tight tolerance component production at Mato Industries' },
  { src: '/images/inside_vmc.jpg',          alt: 'Mazak VMC vertical machining centre for precision components at Mato Industries Delhi' },
  { src: '/images/machine.png',             alt: 'CNC machining workstation with SPC software integration for real-time quality monitoring at Mato Industries' },
];

const images_casting = [
  { src: '/images/gdc.jpg',     alt: 'Gravity die casting facility for CuAl10Fe5Ni5, CuAl11Fe5Ni6 and copper alloy components at Mato Industries Delhi foundry' },
  { src: '/images/foundry.png', alt: 'Sand casting foundry floor for Phosphorous bronze, gun metal, leaded bronze and aluminium bronze alloys at Mato Industries' },
];

const images_quality = [
  { src: '/images/cmm_2.jpeg',       alt: 'Mitutoyo CMM coordinate measuring machine for dimensional inspection of precise components at Mato Industries' },
  { src: '/images/spc_edge.png',     alt: 'SPC Edge statistical process control software monitoring Cp and Cpk on CNC workstation at Mato Industries' },
  { src: '/images/contracer_1.jpeg', alt: 'Mitutoyo Contracer contour measuring instrument for surface profile inspection at Mato Industries quality lab' },
];

const ChevronLeft = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
  </svg>
);

const ChevronRight = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

const Carousel = ({ images }) => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const prev = useCallback(() => setIndex((i) => (i - 1 + images.length) % images.length), [images.length]);
  const next = useCallback(() => setIndex((i) => (i + 1) % images.length), [images.length]);

  useEffect(() => {
    if (paused) return;
    const id = setInterval(next, 3000);
    return () => clearInterval(id);
  }, [paused, next]);

  return (
    <div
      className="relative rounded-2xl overflow-hidden shadow-lg bg-slate-200"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="aspect-[3/2]">
        <img
          src={images[index].src}
          alt={images[index].alt}
          width="600"
          height="400"
          className="w-full h-full object-cover"
        />
      </div>

      {/* Arrows */}
      <button
        onClick={prev}
        aria-label="Previous image"
        className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-navy-900/70 hover:bg-navy-900/90 text-white rounded-full flex items-center justify-center transition-colors duration-200 cursor-pointer"
      >
        <ChevronLeft />
      </button>
      <button
        onClick={next}
        aria-label="Next image"
        className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-navy-900/70 hover:bg-navy-900/90 text-white rounded-full flex items-center justify-center transition-colors duration-200 cursor-pointer"
      >
        <ChevronRight />
      </button>

      {/* Dots */}
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
        {images.map((_, i) => (
          <button
            key={i}
            onClick={() => setIndex(i)}
            aria-label={`Go to image ${i + 1}`}
            className={`w-2 h-2 rounded-full transition-all duration-200 cursor-pointer ${
              i === index ? 'bg-brand-400 w-4' : 'bg-white/60 hover:bg-white'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

const InfraSection = ({ title, bullets, description, extra, images, imageRight = false }) => (
  <section className="mb-16">
    <div className={`flex flex-col md:flex-row gap-10 items-start ${imageRight ? '' : 'md:flex-row-reverse'}`}>
      {/* Text */}
      <div className="md:w-1/2">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-1 h-8 bg-brand-500 rounded-full shrink-0" aria-hidden="true" />
          <h2 className="text-2xl md:text-3xl font-extrabold text-navy-900">{title}</h2>
        </div>
        <ul className="space-y-1.5 mb-5">
          {bullets.map((b) => (
            <li key={b} className="flex items-start gap-2 text-base text-slate-700">
              <span className="w-1.5 h-1.5 bg-brand-500 rounded-full mt-1.5 shrink-0" aria-hidden="true" />
              <span dangerouslySetInnerHTML={{ __html: b }} />
            </li>
          ))}
        </ul>
        <div className="text-slate-600 text-base leading-relaxed space-y-3">{description}</div>
        {extra && <div className="mt-4">{extra}</div>}
      </div>
      {/* Image carousel */}
      <div className="md:w-1/2 w-full">
        <Carousel images={images} />
      </div>
    </div>
  </section>
);

const InfrastructurePage = () => (
  <>
    <Helmet>
      <title>Manufacturing Infrastructure – CNC, VMC & Inspection | Mato Industries</title>
      <meta name="description" content="Mazak VMCs, Doosan & Tsugami CNC lathes, Mitutoyo CMM, Hitachi Spectrometer. Full in-house casting to finished component capability. Mato Industries, Delhi." />
      <link rel="canonical" href="https://www.matoindustries.com/infrastructure" />
      <meta property="og:url" content="https://www.matoindustries.com/infrastructure" />
      <meta property="og:title" content="Manufacturing Infrastructure – CNC, VMC & Inspection | Mato Industries" />
      <meta property="og:description" content="Mazak VMCs, Doosan & Tsugami CNC lathes, Mitutoyo CMM, Hitachi Spectrometer. Full in-house casting to finished component capability." />
    </Helmet>
    <div className="min-h-screen bg-slate-50">

    {/* Page header */}
    <div className="bg-navy-900 text-white py-14 px-4">
      <div className="container mx-auto max-w-7xl">
        <span className="text-brand-400 text-sm font-semibold uppercase tracking-widest">Facilities</span>
        <h1 className="text-4xl md:text-5xl font-extrabold mt-2 mb-3">Our Infrastructure</h1>
        <p className="text-slate-400 text-lg max-w-2xl">
          From foundry to final inspection, every stage of manufacturing is handled under one roof in our Delhi facility.
        </p>
      </div>
    </div>

    <main className="container mx-auto max-w-7xl px-4 sm:px-6 py-12">

      <InfraSection
        title="Casting"
        bullets={[
          '<strong>Sand Casting</strong> — suitable for complex shapes and small-to-medium batch sizes',
          '<strong>Gravity Die Casting</strong> — for higher volume runs with better dimensional consistency',
        ]}
        description={
          <>
            <p>
              Our in-house foundry handles both Sand Casting and Gravity Die Casting for a full range of copper alloys
              including <strong>Gun Metal, Leaded Bronze, Aluminium Bronze, Phosphorous Bronze, and High Tensile Brass</strong>.
              Having casting in-house gives us direct control over alloy composition, grain structure, and porosity — factors
              that directly affect the performance of the finished component.
            </p>
            <p>
              All castings are verified using our <strong>Hitachi Spectrometer</strong> to confirm alloy chemistry before
              machining begins. This eliminates material non-conformances and ensures every component meets customer
              specifications from the first stage of production.
            </p>
          </>
        }
        images={images_casting}
        imageRight
      />

      <InfraSection
        title="CNC & VMC Machining"
        bullets={[
          '<strong>VMCs: Mazak</strong> — vertical machining centres for complex multi-axis operations',
          '<strong>CNC Lathes: Doosan, Tsugami, LMW</strong> — high-speed turning for bushings, washers, and rings',
          '<strong>Workshop machinery:</strong> band saws, surface grinders, drill presses, broaching machines',
        ]}
        description={
          <>
            <p>
              Our machining floor operates Mazak VMCs and CNC lathes from Doosan, Tsugami, and LMW — machines known
              for their rigidity and repeatability when cutting copper alloys. We routinely hold tolerances of
              <strong> ±0.01mm</strong> on critical dimensions such as bore diameter, OD, and face runout.
            </p>
            <p>
              Every CNC workstation is integrated with <strong>SPC Edge software</strong> for real-time statistical
              process control. Operators monitor Cp and Cpk values continuously, allowing us to catch process drift
              before it produces out-of-tolerance parts. This approach eliminates end-of-line sorting and supports
              our zero-defect supply commitment to OEM customers.
            </p>
          </>
        }
        images={images_machines}
      />

      <InfraSection
        title="Quality & Inspection"
        bullets={[
          '<strong>Mitutoyo CMM</strong> — coordinate measuring machine for 3D dimensional verification',
          '<strong>Mitutoyo Contracer</strong> — contour and surface profile measurement',
          '<strong>Hitachi Spectrometer</strong> — alloy chemistry verification at incoming and in-process stages',
          '<strong>SPC Edge Software</strong> — integrated on each machining workstation for real-time Cp/Cpk monitoring',
        ]}
        description={
          <>
            <p>
              Our quality lab is equipped with Mitutoyo CMM and Contracer instruments for precise dimensional and
              surface profile measurement. The Hitachi Spectrometer verifies alloy chemistry at both the casting
              stage and on incoming raw material, ensuring full traceability from raw material to finished component.
            </p>
            <p>
              We operate under <strong>ISO 9001:2015</strong> quality management, with 100% in-process inspection
              on all critical dimensions. Inspection data is digitally recorded and retained for <strong>3 years</strong>,
              supporting customer audit requirements and long-term traceability. Our systems are also certified to
              <strong> ISO 45001:2018</strong> (Health &amp; Safety) and <strong>ISO 14001:2015</strong> (Environmental Management).
            </p>
          </>
        }
        extra={
          <div className="flex flex-wrap gap-2">
            {['ISO 9001:2015', 'ISO 45001:2018', 'ISO 14001:2015'].map((cert) => (
              <span key={cert} className="px-3 py-1.5 bg-brand-50 border border-brand-200 text-brand-700 text-xs font-semibold rounded-lg">
                {cert}
              </span>
            ))}
          </div>
        }
        images={images_quality}
        imageRight
      />

    </main>
    </div>
  </>
);

export default InfrastructurePage;
